import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, first, map, Observable, switchMap } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { PolicyName } from './remediate';
import * as HostedContentFilterPolicyTools from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/exo/hosted-content-filter-policy';
import { tooltips } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/exo/hosted-content-filter-policy';
import {
    ISO_3166_1_COUNTRIES,
    ISO_639_2_LANGUAGES,
} from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/exo/hosted-content-filter-policy/constants';
import { HostedContentFilterPolicySchema } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/exo/hosted-content-filter-policy/model';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { HostedContentFilterPolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/hosted-content-filter-policy/model';
import { QuarantinePolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/quarantine-policy/model';
import { selectSession } from 'projects/angular-clarity/src/app/stores/root.store';
import { isValidDomain, isValidEmail } from 'projects/angular-clarity/src/app/utils/custom-validators';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit, OnChanges {
    form: FormGroup = null;
    defaultPolicy: HostedContentFilterPolicy;
    tooltips = tooltips;
    override: string[] = [];
    languages = ISO_639_2_LANGUAGES;
    countries = ISO_3166_1_COUNTRIES;

    intraOrgFilterStateLabels: Record<string, string> = {
        Disabled: 'None',
        HighConfidencePhish: 'High confidence phishing messages',
        Phish: 'Phishing and high confidence phishing messages',
        HighConfidenceSpam: 'All phishing and high-confidence spam messages',
        Spam: 'All phishing and spam messages',
    };

    testModeActionLabels: Record<string, string> = {
        None: 'None',
        AddXHeader: 'Add X-Header',
        BccMessage: 'Send Bcc Message',
    };

    spamActionLabels: Record<string, string> = {
        MoveToJmf: 'Move message to junk email folder',
        AddXHeader: 'Add X-header',
        ModifySubject: 'Prepend subject line with text',
        Redirect: 'Redirect message to email address',
        Delete: 'Delete message',
        Quarantine: 'Quarantine message',
        NoAction: 'No action',
    };

    tenant_id$: Observable<string> = this.store.pipe(
        select(selectSession),
        map((sess) => sess.session.clientId),
    );

    quarantinePolicyLookup$ = this.getQuarantinePolicy();
    private quarantinePolicies: QuarantinePolicy[] = [];

    constructor(
        private fb: FormBuilder,
        private store: Store<any>,
    ) {
        super();
    }

    ngOnInit(): void {
        this.override = this.baselineTemplateItem?.override || [];
        this.defaultPolicy = this.data.find((res) => res.Name === PolicyName);
        const schema: HostedContentFilterPolicySchema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = HostedContentFilterPolicyTools.initForm(schema, this.fb);
        HostedContentFilterPolicyTools.setupConditionalValidators(this.form);
        this.observeFormChange();
        this.quarantinePolicyLookup$.subscribe((policies) => {
            this.quarantinePolicies = policies;
            this.setupQuarantineDefaults();
        });
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.defaultPolicy = this.data.find((res) => res.Name === PolicyName);
    }

    observeFormChange(): void {
        const schema: HostedContentFilterPolicySchema = JSON.parse(JSON.stringify(this.baseline.schema));

        this.form.valueChanges.subscribe(() => {
            const updatedSchema = HostedContentFilterPolicyTools.updateSchema(this.formValue, schema);

            if (this.form.invalid) {
                this.baselineErrorChange.next({
                    save: true,
                    remediate: true,
                });
            } else {
                this.baselineErrorChange.next({
                    save: false,
                    remediate: false,
                });
            }

            // if (this.form.invalid) {
            //     Object.keys(this.form.controls)
            //         .filter((k) => this.form.get(k).invalid)
            //         .forEach((k) =>
            //             console.warn('invalid control:', k, this.form.get(k).errors, 'value:', this.form.get(k).value),
            //         );
            // }

            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(updatedSchema)),
            });
        });
    }

    initForm(schema: HostedContentFilterPolicySchema): void {
        this.form = HostedContentFilterPolicyTools.initForm(schema, this.fb);
    }

    get formValue() {
        return this.form.getRawValue();
    }

    get isAddXHeaderSelected(): boolean {
        const v = this.formValue;
        return ['SpamAction', 'HighConfidenceSpamAction', 'PhishSpamAction', 'BulkSpamAction'].some(
            (k) => v?.[k] === 'AddXHeader',
        );
    }

    get isModifySubjectSelected(): boolean {
        const v = this.formValue;
        return ['SpamAction', 'HighConfidenceSpamAction', 'PhishSpamAction', 'BulkSpamAction'].some(
            (k) => v?.[k] === 'ModifySubject',
        );
    }

    get isRedirectSelected(): boolean {
        const v = this.formValue;
        return [
            'SpamAction',
            'HighConfidenceSpamAction',
            'PhishSpamAction',
            'BulkSpamAction',
            'HighConfidencePhishAction',
        ].some((k) => v?.[k] === 'Redirect');
    }

    get hasRedirectToRecipientsError() {
        return this.errorsString.includes('RedirectToRecipients');
    }

    getLanguageNames(codes: string[]): string {
        return codes?.map((code) => this.languages.find((l) => l.code === code)?.name || code).join(', ') || '-';
    }

    getCountryNames(codes: string[]): string {
        return codes?.map((code) => this.countries.find((c) => c['alpha-2'] === code)?.name || code).join(', ') || '-';
    }

    addEmailTag = (term: string): string | null => (isValidEmail(term.trim()) ? term.trim() : null);
    addDomainTag = (term: string): string | null => (isValidDomain(term.trim()) ? term.trim() : null);

    private setupQuarantineDefaults(): void {
        const quarantineFields: Array<[string, string]> = [
            ['SpamAction', 'SpamQuarantineTag'],
            ['HighConfidenceSpamAction', 'HighConfidenceSpamQuarantineTag'],
            ['PhishSpamAction', 'PhishQuarantineTag'],
            ['HighConfidencePhishAction', 'HighConfidencePhishQuarantineTag'],
            ['BulkSpamAction', 'BulkQuarantineTag'],
        ];

        quarantineFields.forEach(([actionKey, tagKey]) => {
            this.form.get(actionKey).valueChanges.subscribe((value) => {
                if (value === 'Quarantine' && !this.form.get(tagKey).value) {
                    const fallback =
                        this.quarantinePolicies.find((p) => p.Name === 'DefaultFullAccessPolicy') ??
                        this.quarantinePolicies[0];
                    if (fallback) {
                        this.form.get(tagKey).setValue(fallback.Name);
                    }
                }
            });
        });
    }

    getQuarantinePolicy(): Observable<QuarantinePolicy[]> {
        return this.tenant_id$.pipe(
            switchMap((tid) =>
                combineLatest([
                    this.store.select(client(tid).powershell.exoQuarantinePolicy.all),
                    this.store.select(client(tid).powershell.exoQuarantinePolicy.status),
                ]),
            ),
            first(([, status]) => status.loaded),
            map(([data]) => data),
        );
    }
}
